import { api } from "../../lib/api";
import { getAuthToken } from "../../lib/auth";
import { type CategoryId, type CalendarEvent } from "./calendarTypes";

const STORAGE_PREFIX = "calendar:events:";
const CATEGORY_PREFIX = "calendar:categories:";
const SELECTION_PREFIX = "calendar:selection:";
const SAMPLE_EVENT_TITLES = new Set([
  "sprint planning",
  "design review",
  "cs midterm",
  "family dinner",
  "co-op night",
  "office hours",
  "1:1 check-in",
]);

export function loadEvents(fid: string): CalendarEvent[] {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${fid}`);
    if (raw) {
      const parsed = JSON.parse(raw) as CalendarEvent[];
      if (Array.isArray(parsed)) {
        return parsed.filter((ev) => !SAMPLE_EVENT_TITLES.has((ev.title || "").trim().toLowerCase()));
      }
    }
  } catch {
    /* ignore, fall back to sample */
  }
  return [];
}

export function persistEvents(fid: string, events: CalendarEvent[]) {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${fid}`, JSON.stringify(events));
  } catch {
    /* ignore storage errors */
  }
}

export function loadCustomCategories(fid: string): Record<string, { id: string; label: string }[]> {
  try {
    const raw = localStorage.getItem(`${CATEGORY_PREFIX}${fid}`);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, { id: string; label: string }[]>;
    if (parsed && typeof parsed === "object") return parsed;
    return {};
  } catch {
    return {};
  }
}

export function persistCustomCategories(fid: string, cats: Record<string, { id: string; label: string }[]>) {
  try {
    localStorage.setItem(`${CATEGORY_PREFIX}${fid}`, JSON.stringify(cats));
  } catch {
    /* ignore storage errors */
  }
}

export async function fetchRemoteEvents(fid: string): Promise<CalendarEvent[] | null> {
  const tok = getAuthToken() || "";
  if (!tok) return null;
  try {
    const res = await api.getAuth(`/calendar/${encodeURIComponent(fid)}/events`, tok);
    let arr = (res as any)?.events || res;
    if (typeof arr === "string") {
      try { arr = JSON.parse(arr); } catch { arr = []; }
    }
    if (Array.isArray(arr)) {
      return (arr as CalendarEvent[]).filter((ev) => !SAMPLE_EVENT_TITLES.has((ev.title || "").trim().toLowerCase()));
    }
  } catch {
    /* ignore remote fetch errors */
  }
  return null;
}

export async function persistRemoteEvents(fid: string, events: CalendarEvent[]) {
  const tok = getAuthToken() || "";
  if (!tok) return;
  try {
    await api.putAuth(`/calendar/${encodeURIComponent(fid)}/events`, { events }, tok);
  } catch {
    /* ignore remote persist errors */
  }
}

export function loadSelection(fid: string): Record<string, Set<CategoryId>> {
  try {
    const raw = localStorage.getItem(`${SELECTION_PREFIX}${fid}`);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, CategoryId[]>;
    const out: Record<string, Set<CategoryId>> = {};
    if (parsed && typeof parsed === "object") {
      for (const [pid, arr] of Object.entries(parsed)) {
        out[pid] = new Set(Array.isArray(arr) ? arr : []);
      }
    }
    return out;
  } catch {
    return {};
  }
}

export function persistSelection(fid: string, selection: Record<string, Set<CategoryId>>) {
  try {
    const jsonReady: Record<string, CategoryId[]> = {};
    for (const [pid, set] of Object.entries(selection)) {
      jsonReady[pid] = Array.from(set || []);
    }
    localStorage.setItem(`${SELECTION_PREFIX}${fid}`, JSON.stringify(jsonReady));
  } catch {
    /* ignore storage errors */
  }
}
